/* eslint-disable indent */
/**
 * Implements a DEX info block
 */
import { ITokenInfo, Network } from '@kattana-io/common';
import Image from '../../../atoms/Image';
import Text from '../../../atoms/Text';
import InfoCoin from '../../../molecules/InfoCoin';
import CoinInfoField from '../CoinInfoField';
import { formatTimestamp, toFixed2 } from '../../DexTrades/formatters';
import { formatUSD } from '../../Modal/TickersModal/helpers';
import { onCopyAddress } from '../../../helpers/addressUtils';
import { ITickerLivePrice } from '../../Watchlist/hooks/useTickerLivePrice';
import { ICurrentState } from '../../../reducers/current';
import { PoolData } from './useDexPoolInfo';
import NumberAnimation from '../../../molecules/NumberAnimation';
import NumberDisplay from '../../../molecules/NumberDisplay';

import coingecko from '../../../assets/images/coingecko.svg';
import coinmarketcap from '../../../assets/images/coinmarketcap.svg';
import metamask from '../../../assets/images/metamask.svg';
import twitter from '../../../pages/TokenPage/images/twitter.svg';
import telegram from '../../../pages/TokenPage/images/telegram.svg';
import bubblemapsIcon from '../../../assets/images/bubblemaps.svg';
import { IPriceChange } from '../../../helpers/priceChange';

const BubblemapsNetwork: Record<string, string> = {
  ETH: 'eth',
  BSC: 'bsc',
  FTM: 'ftm',
  AVAX: 'avax',
  CRONOS: 'cro',
  ARBITRUM: 'arbi',
  POLYGON: 'poly',
};

const decorateValue = (
  key: string,
  value: number,
  timezone?: string
): string => {
  switch (key) {
    case 'Daily volume':
      return formatUSD(Math.round(+value));
    case 'Pool created':
      return formatTimestamp(+value, timezone);
    case 'Pool liquidity':
      return formatUSD(Math.round(+value));
    case 'APY':
      return `${toFixed2(value)} %`;
    default:
      return value?.toString() ?? '';
  }
};

const addToWallet = async (props: IDexCoinInfoProps) => {
  try {
    const wasAdded = await (window as any).ethereum.request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: props.tokenAddress, // The address that the token is at.
          symbol: props.slug, // A ticker symbol or shorthand, up to 5 chars.
          decimals: props.tokenDecimals, // The number of decimals in the token
          image: props.logo_url, // A string url of the token logo
        },
      },
    });

    if (!wasAdded) {
      console.warn('Asset wasnt added due to user rejection');
    }
  } catch (error) {
    console.warn(error);
  }
};

interface IDexCoinInfoProps
  extends ITickerLivePrice,
    ICurrentState,
    IPriceChange,
    ITokenInfo {
  timezone?: string;
  info: PoolData;
  tokenAddress: string;
  tokenDecimals: number;
  network: Network;
}

const DexCoinInfo = (props: IDexCoinInfoProps) => {
  const bubblemaps = BubblemapsNetwork[props.network.getSlug()];

  let priceColor = 'text--grey-200';
  if (props.side === 'buy') {
    priceColor = 'text--green';
  } else if (props.side === 'sell') {
    priceColor = 'text--red';
  }

  return (
    <>
      <div className='info_coin__mt'>
        <InfoCoin
          {...props}
          address={props.tokenAddress}
          onCopyAddress={() => onCopyAddress(props.tokenAddress)}
        />
      </div>
      <div className='row'>
        <div className='info_coin__icons'>
          {props.website && (
            <a
              href={props.website}
              className='icon icon_link_img icon_link_img--font icon-discovery'
              target='_blank'
              rel='noreferrer'
            />
          )}
          {props.coingecko_id && (
            <a
              href={`https://www.coingecko.com/en/coins/${props.coingecko_id}`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={coingecko} size='s' />
            </a>
          )}
          {props.coinmarketcap_id && (
            <a
              href={`https://coinmarketcap.com/currencies/${props.coinmarketcap_id}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={coinmarketcap} size='s' />
            </a>
          )}
          {props.telegram && (
            <a
              href={`https://t.me/${props.telegram}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={telegram} size='s' />
            </a>
          )}
          {props.twitter_id && (
            <a
              href={`https://twitter.com/${props.twitter_id}/`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={twitter} size='s' />
            </a>
          )}
          {!props.network.isTron() && (
            <div className='icon_link_img' onClick={() => addToWallet(props)}>
              <Image src={metamask} size='s' />
            </div>
          )}
          {bubblemaps && (
            <a
              href={`https://app.bubblemaps.io/${bubblemaps}/token/${props.tokenAddress}`}
              className='icon_link_img'
              target='_blank'
              rel='noreferrer'
            >
              <Image src={bubblemapsIcon} size='s' />
            </a>
          )}
        </div>
        <div className='fragment info_coin__price'>
          <NumberAnimation
            value={props.price.rawPrice}
            round={2}
            startSymbol={props.price.sign}
            textProps={{
              size: 18,
              className: priceColor,
            }}
          />
          {props.priceChange === 0 ? (
            <Text size={12} className='info_coin__price__change text--grey-200'>
              (0%) $0
            </Text>
          ) : (
            <Text
              size={12}
              className={`info_coin__price__change ${
                props.priceChangePercent < 0 ? 'text--red' : 'text--green'
              }`}
            >
              <NumberDisplay
                value={props.priceChangePercent}
                round={2}
                startSymbol='('
                endSymbol='%)'
                string
              />
              &nbsp;
              <NumberDisplay
                value={props.priceChange}
                round={2}
                startSymbol='$'
                string
              />
            </Text>
          )}
        </div>
      </div>
      {Object.keys(props.info).map((key) => (
        <CoinInfoField
          key={key}
          name={key}
          value={decorateValue(key, props.info[key], props.timezone)}
        />
      ))}
    </>
  );
};

export default DexCoinInfo;
